import { ArrowLeftRight, Check } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { Highlight } from "@/components/highlight";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  type StartViewAs,
  useFetchApps,
  useStartViewAs,
  useViewAsOptions,
  type ViewAsPerson,
  type ViewAsState,
} from "@/lib/api";
import { apiErrorMessage, isToastedError } from "@/lib/api-errors";
import { logger } from "@/lib/logger";
import { fullName } from "@/lib/navigation";
import { matchesPerson } from "@/lib/people";
import { roleLabel } from "@/lib/roles";
import { dismissToasts, toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import {
  isAppPage,
  isCurrentView,
  pathAfterSwitch,
  reloadDashboard,
  VIEW_AS_LAYOUT,
  VIEW_AS_ROLES,
} from "@/lib/view-as";

/** One choice in the list: a person or a role, and what starting it sends. */
interface Choice {
  key: string;
  /** A person matches what was typed, which their name and email mark; a role is always listed. */
  kind: "person" | "role";
  target: StartViewAs;
  label: string;
  /** A person's email under their name; none for a role. */
  detail?: string;
  /** What the choice is, on the right: a person's role. */
  note?: string;
}

/** A person as the list shows them: by name (else email), the email under it. */
export function personChoice(person: ViewAsPerson): Choice {
  const name = fullName(person);
  return {
    key: `person:${person.id}`,
    kind: "person",
    target: { person_id: person.id },
    label: name ?? person.email,
    detail: name ? person.email : undefined,
    note: roleLabel(person.role),
  };
}

/** The roles as the list shows them, after the people. */
export function roleChoices(): Choice[] {
  return VIEW_AS_ROLES.map((role) => ({
    key: `role:${role}`,
    kind: "role",
    target: { role },
    label: roleLabel(role),
  }));
}

/** The people that match what was typed, on their name and email. */
export function matchingPeople(people: readonly ViewAsPerson[], query: string): ViewAsPerson[] {
  return people.filter((person) =>
    matchesPerson({ email: person.email, name: fullName(person) }, query),
  );
}

interface ViewAsSwitcherProps {
  /** The view that is on: its person or role is marked, and picking it again changes nothing. */
  view: ViewAsState;
}

/**
 * The bar's "Wissel": a button that opens a search box over everyone the
 * developer can view the dashboard as, then the three roles, as
 * `<PersonPicker>` lists people. Typing filters the people by name or email;
 * the roles stay listed. The view that is on is ticked. Picking another
 * starts it (the server switches the view) and loads the page again where it
 * is, so every query answers for the new view; an app page the new view may
 * not open loads Home instead, where the bar names the new view. A refused
 * switch closes the list and says why in an error toast. The list opens top
 * right, where the toasts sit, so opening it closes any toast on screen.
 * Keyboard: the arrows move,
 * Enter picks, Escape closes. The people load when the list first opens.
 * On a phone the button shows its icon only, its name for screen readers.
 */
export function ViewAsSwitcher({ view }: ViewAsSwitcherProps) {
  const { t } = useTranslation();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const options = useViewAsOptions({ enabled: open });
  const start = useStartViewAs();
  const fetchApps = useFetchApps();
  const { pathname } = useLocation();
  // Started: the page is about to load again for the new view.
  const switching = start.isPending || start.isSuccess;

  const personChoices = matchingPeople(options.data?.people ?? [], query).map(personChoice);
  const roles = roleChoices();
  const choices = [...personChoices, ...roles];
  const current = choices.length > 0 ? Math.min(active, choices.length - 1) : null;
  const optionId = (index: number) => `${listId}-${index}`;

  // Keep the option the arrows reached in view.
  useEffect(() => {
    if (open) {
      document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: "nearest" });
    }
  }, [active, open, listId]);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (next) {
      // The list opens top right, where the toasts sit: none may cover it.
      dismissToasts();
    } else {
      setQuery("");
      setActive(0);
    }
  };

  // An app page stays open only when the new view may open the app too, else Home.
  const reloadForNewView = () => {
    if (!isAppPage(pathname)) {
      reloadDashboard();
      return;
    }
    fetchApps().then(
      (overview) => reloadDashboard(pathAfterSwitch(pathname, overview.apps)),
      (error: unknown) => {
        logger.warn("[view-as] apps unread after the switch", error);
        reloadDashboard();
      },
    );
  };

  const pick = (choice: Choice) => {
    if (switching) {
      return;
    }
    if (isCurrentView(view, choice.target)) {
      changeOpen(false);
      return;
    }
    logger.debug("[view-as]", "switch →", choice.key);
    start.mutateAsync(choice.target).then(reloadForNewView, (error: unknown) => {
      logger.warn("[view-as] switch refused", error);
      // Closed first, so the error toast top right lands on the page, not over the list.
      changeOpen(false);
      if (!isToastedError(error)) {
        toast.error(() => apiErrorMessage(error, "startViewAs"));
      }
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const count = choices.length;
    if (event.key === "ArrowDown" && count > 0) {
      event.preventDefault();
      setActive((index) => (index + 1) % count);
    } else if (event.key === "ArrowUp" && count > 0) {
      event.preventDefault();
      setActive((index) => (index - 1 + count) % count);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const choice = current === null ? undefined : choices[current];
      if (choice) {
        pick(choice);
      }
    }
  };

  const renderChoice = (choice: Choice, index: number) => {
    const on = isCurrentView(view, choice.target);
    return (
      <li
        key={choice.key}
        id={optionId(index)}
        role="option"
        aria-selected={index === current}
        aria-disabled={on || undefined}
        // Keep the focus in the search box, so typing goes on after a click.
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => pick(choice)}
        onMouseMove={() => setActive(index)}
        className={cn(
          "flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm pointer-coarse:py-2.5",
          index === current && "bg-accent-light",
        )}
      >
        <span aria-hidden="true" className="flex size-4 shrink-0 items-center justify-center">
          {on && <Check className="text-primary size-4" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="text-foreground block truncate font-medium">
            {choice.kind === "role" ? (
              choice.label
            ) : (
              <Highlight text={choice.label} query={query} />
            )}
          </span>
          {choice.detail && (
            <span className="text-muted block truncate text-xs">
              <Highlight text={choice.detail} query={query} />
            </span>
          )}
        </span>
        {choice.note && <span className="text-muted shrink-0 text-xs">{choice.note}</span>}
      </li>
    );
  };

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          aria-label={t("viewAs.bar.switch")}
          className={VIEW_AS_LAYOUT.button}
        >
          <ArrowLeftRight aria-hidden="true" className="size-4" />
          <span className="max-sm:hidden">{t("viewAs.bar.switch")}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 max-w-[calc(100vw-2rem)] p-0">
        <div className="border-b p-2">
          <Input
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={current === null ? undefined : optionId(current)}
            aria-label={t("viewAs.bar.searchPerson")}
            placeholder={t("viewAs.bar.searchPerson")}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            className="h-9 pointer-coarse:h-10"
          />
        </div>
        <ul
          id={listId}
          role="listbox"
          aria-label={t("viewAs.bar.switch")}
          aria-busy={switching || undefined}
          className="scrollbar-themed max-h-72 overflow-y-auto p-1"
        >
          <ChoiceGroup label={t("viewAs.people")}>
            {personChoices.map((choice, index) => renderChoice(choice, index))}
            {options.isPending ? (
              <GroupNote>{t("common.loadingEllipsis")}</GroupNote>
            ) : options.isError ? (
              <GroupNote>{t("viewAs.loadError")}</GroupNote>
            ) : (
              personChoices.length === 0 && <GroupNote>{t("viewAs.bar.noPeople")}</GroupNote>
            )}
          </ChoiceGroup>
          <ChoiceGroup label={t("viewAs.roles")}>
            {roles.map((choice, index) => renderChoice(choice, personChoices.length + index))}
          </ChoiceGroup>
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** A group of the list under its small label, which names it for screen readers. */
function ChoiceGroup({ label, children }: { label: string; children: ReactNode }) {
  const labelId = useId();
  return (
    <li role="presentation">
      <p id={labelId} className="text-muted px-2 pt-2 pb-1 text-xs font-semibold">
        {label}
      </p>
      <ul role="group" aria-labelledby={labelId}>
        {children}
      </ul>
    </li>
  );
}

/** A line in a group that is no choice: loading, a failure, nobody found. */
function GroupNote({ children }: { children: ReactNode }) {
  return (
    <li role="presentation" className="text-muted px-2 py-1.5 text-sm">
      {children}
    </li>
  );
}
